import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSearchParams } from "react-router-dom";
import { Search, Settings, Plug, Shield, Zap, UserCheck, Apple } from "lucide-react";
import { patchPolicySchema, PatchPolicyFormValues } from "@/components/patching/schema";
import { Form } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { GlassCard, GlassCardContent } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// Import category components
import { SystemIntegrationSettings } from "@/components/settings/SystemIntegrationSettings";
import { IdentityProvidersSettings } from "@/components/settings/IdentityProvidersSettings";
import { GeneralSettings } from "@/components/settings/GeneralSettings";
import { AppleSettings } from "@/components/settings/AppleSettings";

type SettingsCategory = {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  count?: number;
  status?: "active" | "warning" | "inactive";
};

const categories: SettingsCategory[] = [
  {
    id: "general",
    label: "General Settings",
    icon: Settings,
    description: "Basic app preferences and notifications",
    count: 5,
    status: "active"
  },
  {
    id: "system",
    label: "System Integrations",
    icon: Plug,
    description: "Email notifications and SMTP configuration",
    count: 1,
    status: "active"
  },
  {
    id: "apple",
    label: "Apple",
    icon: Apple,
    description: "APNS certificates and Apple Business Manager",
    count: 2,
    status: "active"
  },
  {
    id: "identity",
    label: "Identity Providers",
    icon: UserCheck,
    description: "Connect directory services and SSO",
    count: 2,
    status: "active"
  },
  {
    id: "security",
    label: "Security",
    icon: Shield,
    description: "Security policies and access controls",
    count: 0,
    status: "inactive"
  },
  {
    id: "advanced",
    label: "Advanced",
    icon: Zap,
    description: "Advanced configurations and integrations",
    count: 0,
    status: "inactive"
  }
];

export default function SettingsPage() {
  const [searchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get("category");
  const [selectedCategory, setSelectedCategory] = useState(categoryFromUrl || "general");
  const [searchQuery, setSearchQuery] = useState("");

  // Update selected category when URL parameter changes
  useEffect(() => {
    if (categoryFromUrl && categories.find(cat => cat.id === categoryFromUrl)) {
      setSelectedCategory(categoryFromUrl);
    }
  }, [categoryFromUrl]);

  // Disable Layout main scrolling while on Settings page
  useEffect(() => {
    const mainEl = document.querySelector('main') as HTMLElement | null;
    if (!mainEl) return;
    const prevOverflowY = mainEl.style.overflowY;
    const prevOverscroll = mainEl.style.overscrollBehavior;
    mainEl.style.overflowY = 'hidden';
    mainEl.style.overscrollBehavior = 'contain';
    return () => {
      mainEl.style.overflowY = prevOverflowY;
      mainEl.style.overscrollBehavior = prevOverscroll;
    };
  }, []);

  const form = useForm<PatchPolicyFormValues>({
    resolver: zodResolver(patchPolicySchema),
    defaultValues: {
      name: "",
      description: "",
      schedule_type: "manual",
      restart_required: false,
      defer_max_times: 3,
      defer_duration_minutes: 60,
      enabled: true,
      update_method: "default",
      force_install_days: 90,
      allowed_days: [],
      show_notifications: true,
      notifications_timing: "all",
      allow_major_updates: true,
      allow_minor_updates: true,
      allow_security_updates: true,
      auto_download: true,
      auto_install_os_updates: false,
      auto_install_security_updates: true,
      enable_rsr_installation: false,
      enable_rsr_rollback: false,
      smtp_host: "",
      smtp_port: 587,
      smtp_username: "",
      smtp_password: "",
      smtp_from_address: "",
      apns_certificate: "",
      apns_certificate_password: "",
      abm_token: "",
      abm_organization_id: "",
      abm_server_token: ""
    }
  });

  const filteredCategories = categories.filter(category =>
    category.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 border-green-200";
      case "warning":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const renderCategoryContent = () => {
    switch (selectedCategory) {
      case "general":
        return <GeneralSettings form={form} />;
      case "system":
        return <SystemIntegrationSettings form={form} />;
      case "apple":
        return <AppleSettings form={form} />;
      case "identity":
        return <IdentityProvidersSettings />;
      case "security":
        return (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Shield className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Security Settings</h3>
            <p className="text-muted-foreground max-w-md">
              Security configurations will be available in a future update. Configure access controls, audit logs, and security policies.
            </p>
          </div>
        );
      case "advanced":
        return (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Zap className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Advanced Settings</h3>
            <p className="text-muted-foreground max-w-md">
              Advanced configurations and integrations will be available in a future update. Manage custom workflows and API integrations.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  const selectedCategoryData = categories.find(cat => cat.id === selectedCategory);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Fixed Header */}
      <div className="px-6 py-6 bg-background relative z-10 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Settings</h1>
            <p className="text-muted-foreground mt-1">Manage your application settings and integrations</p>
          </div>
        </div>
      </div>

      {/* Search Bar - Above Both Panels */}
      <div className="px-6 pb-4 relative z-20">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search settings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 focus-visible:ring-offset-0"
          />
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex gap-6 flex-1 overflow-hidden px-6 pb-6 min-h-0">
        {/* Sidebar Navigation */}
        <div className="w-80 flex flex-col h-full min-h-0">
          {/* Categories */}
          <div 
            className="flex-1 overflow-y-auto overscroll-contain px-2 pt-2 pb-2 space-y-2"
            style={{ scrollbarWidth: 'thin' }}
          >
            {filteredCategories.map((category) => (
              <GlassCard
                key={category.id}
                variant="default"
                hover={true}
                className={cn(
                  "cursor-pointer transition-all",
                  selectedCategory === category.id
                    ? "ring-2 ring-primary/30 shadow-xl"
                    : ""
                )}
                onClick={() => setSelectedCategory(category.id)}
              >
                <GlassCardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <category.icon className="h-5 w-5 text-primary mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-sm">{category.label}</h3>
                        <div className="flex items-center gap-2">
                          {category.count !== undefined && category.count > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {category.count}
                            </Badge>
                          )}
                          {category.status && (
                            <div className={cn(
                              "w-2 h-2 rounded-full",
                              category.status === "active" ? "bg-green-500" :
                              category.status === "warning" ? "bg-yellow-500" :
                              "bg-gray-300"
                            )} />
                          )}
                        </div>
                      </div>
                      
                    </div>
                  </div>
                </GlassCardContent>
              </GlassCard>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            <Form {...form}>
              <form className="space-y-6">
                {renderCategoryContent()}
              </form>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}